import type { AddressValue } from '../../../../components/inputs/AddressPicker';
import { uid } from '../../../../lib/uid';

/**
 * Test data behind Autogenerate on the property form.
 *
 * Every value here stays in `rsys_` — a property name, a street, a title number — so it is
 * plausible rather than obviously synthetic. That is the same test `sampleEducation` passes and
 * `samplePersonnel` deliberately fails: **the rule is about where a value LANDS**, and nothing
 * on this form reaches the identity store. The custodian is picked by KEY from the employees the
 * form already loaded, so no name is invented either.
 */

const PROPERTY_NAMES = [
  'Main Building',
  'Annex Building',
  'North Wing',
  'Records Warehouse',
  'Motor Pool Compound',
  'Training Centre',
] as const;

const STREETS = [
  '12 Sumulong Highway',
  '284 Ortigas Avenue Extension',
  'Block 7 Lot 12, Marcos Highway',
  '5th Floor, Cyberpark Tower 2',
] as const;

/** Real provinces and cities, because the picker writes NAMES and a fake one reads as broken. */
const PLACES: AddressValue[] = [
  { province: 'Rizal', cityMunicipality: 'Antipolo', barangay: null },
  { province: 'Metro Manila', cityMunicipality: 'Pasig', barangay: null },
  { province: 'Metro Manila', cityMunicipality: 'Quezon City', barangay: null },
  { province: 'Laguna', cityMunicipality: 'Calamba', barangay: null },
];

const NOTES = [
  'Occupied since the last reorganization.',
  'Shared with the records office on the second floor.',
  'Due for structural inspection this year.',
] as const;

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)]!;
}

/** A calendar date `n` days from today, as `YYYY-MM-DD`. Never an instant. */
function inDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

export function sampleRealProperty(): {
  propertyCode: string;
  name: string;
  address: AddressValue;
  streetAddress: string;
  floorAreaSqm: string;
  floorCount: string;
  unitCount: string;
  titleNumber: string;
  lotNumber: string;
  acquisitionCost: string;
  acquisitionDate: string;
  monthlyRent: string;
  leaseStartDate: string;
  leaseEndDate: string;
  notes: string;
} {
  /*
    BOTH tenure blocks are filled, whichever one is on screen. The generator does not decide the
    tenure — that flag reveals a whole section, and a button producing a different form on each
    click is worse than one that fills less — but it fills what sits behind it either way, so one
    click reveals a completed section rather than an empty one.
  */
  const leaseStart = inDays(-(30 + Math.floor(Math.random() * 400)));

  return {
    // Suffixed, because Autogenerate is most useful clicked repeatedly and the code is unique
    // among live properties — without it the second click is a rejection.
    propertyCode: `PROP-${uid().slice(0, 6).toUpperCase()}`,
    name: pick(PROPERTY_NAMES),
    address: pick(PLACES),
    streetAddress: pick(STREETS),

    floorAreaSqm: String(200 + Math.floor(Math.random() * 4_800)),
    floorCount: String(1 + Math.floor(Math.random() * 12)),
    unitCount: String(1 + Math.floor(Math.random() * 40)),

    titleNumber: `TCT-${100_000 + Math.floor(Math.random() * 800_000)}`,
    lotNumber: `LOT-${1 + Math.floor(Math.random() * 40)}`,
    acquisitionCost: String((5 + Math.floor(Math.random() * 90)) * 1_000_000),

    // In the PAST: a property is acquired before it is registered, and a future acquisition is
    // the one value here a reader would read as wrong.
    acquisitionDate: inDays(-(365 + Math.floor(Math.random() * 3_000))),

    monthlyRent: String((20 + Math.floor(Math.random() * 180)) * 1_000),
    leaseStartDate: leaseStart,

    /*
      In the FUTURE, and deliberately not near-term. A lapsed lease is legitimate to record, but
      a generator producing one would put every generated property on the renewal list the moment
      it saved — exercising the wrong state and reading as a defect.
    */
    leaseEndDate: inDays(200 + Math.floor(Math.random() * 900)),

    notes: pick(NOTES),
  };
}
